import { FileQuestion } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RfiSendPanel } from "./rfi/RfiSendPanel";
import { RfiItem, RfiActionBar } from "./rfi/RfiParts";
import { useRfiPackage } from "@/hooks/useRfiPackage";

const chipClass = (p, activeId) => {
  if (activeId === p.id) return "border-amber-500 text-amber-300";
  if (p.overdue) return "border-red-500/60 text-red-300";
  return "border-slate-700 text-slate-400 hover:border-slate-500";
};

function PackageChips({ rfi, selected }) {
  const { pkgs, active, openPkg, startNew } = rfi;
  if (!pkgs.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="rfi-package-list">
      {active && <button onClick={() => startNew(false)} className="border border-amber-500/50 text-amber-300 rounded px-2 py-0.5" data-testid="rfi-new-package-button">+ New from selection ({selected.length})</button>}
      {pkgs.map((p) => <button key={p.id} onClick={() => openPkg(p.id)} className={`border rounded px-2 py-0.5 font-mono ${chipClass(p, active?.id)}`} data-testid={`rfi-package-chip-${p.id}`}>{p.number} · {p.answered}/{p.total} answered · {p.status}{p.overdue ? ` · OVERDUE ${p.days_overdue}d` : ""}</button>)}
    </div>
  );
}

function HeaderFields({ rfi, canEdit }) {
  const { form, setForm, contacts, active, items } = rfi;
  return (
    <>
      <div className="grid sm:grid-cols-3 gap-3 text-xs">
        <label className="block"><span className="label-cap">Addressed to</span>
          <select value={form.to_contact_id} onChange={(e) => setForm({ ...form, to_contact_id: e.target.value })} disabled={!canEdit} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5" data-testid="rfi-to-select">
            <option value="">Architect / Engineer of Record (no contact)</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
          </select></label>
        <label className="block"><span className="label-cap">Response requested by</span>
          <input type="date" value={form.response_due} onChange={(e) => setForm({ ...form, response_due: e.target.value })} disabled={!canEdit} className="mt-1 w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5" data-testid="rfi-due-input" /></label>
        <div className="text-[11px] text-slate-500 self-end">{active?.overdue ? <span className="text-red-400 font-semibold" data-testid="rfi-overdue-flag">OVERDUE — response was due {active.response_due} ({active.days_overdue} day{active.days_overdue === 1 ? "" : "s"} ago)</span> : <>Each item is numbered <span className="font-mono text-slate-300">{items[0]?.rfi_no || "RFI-001.01"}</span>… and references its spec section, page, document and finding.</>}</div>
      </div>
      <textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} disabled={!canEdit} rows={2} maxLength={1500} placeholder="Optional introduction (replaces the default paragraph)" className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-xs" data-testid="rfi-intro-input" />
    </>
  );
}

export function RfiPackageDialog({ projectId, selected, findings, open, onOpenChange, canEdit, onCreated }) {
  const rfi = useRfiPackage({ projectId, selected, findings, open, onCreated });
  const { active, items } = rfi;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="rfi-package-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><FileQuestion className="w-4 h-4 text-amber-400" /> {active ? `${active.number} — Request for Information` : `New RFI package (${selected.length} finding${selected.length === 1 ? "" : "s"})`}</DialogTitle></DialogHeader>
        <PackageChips rfi={rfi} selected={selected} />
        <HeaderFields rfi={rfi} canEdit={canEdit} />
        <div className="space-y-2" data-testid="rfi-items">
          {items.map((it) => <RfiItem key={it.finding_id} it={it} canEdit={canEdit} rfi={rfi} />)}
          {!items.length && <div className="text-slate-500 text-xs">Select findings in the table (checkboxes) to build a new package.</div>}
        </div>
        <RfiActionBar canEdit={canEdit} selected={selected} rfi={rfi} />
        {active && canEdit && <RfiSendPanel active={active} members={rfi.members} contacts={rfi.contacts} picked={rfi.picked} flip={rfi.flip} note={rfi.note} setNote={rfi.setNote} contactForm={rfi.contactForm} setContactForm={rfi.setContactForm} addContact={rfi.addContact} send={rfi.send} busy={rfi.busy} sends={rfi.sends} />}
      </DialogContent>
    </Dialog>
  );
}
